import React, { useCallback, useEffect, useState } from "react";
import { Image, StyleSheet, View, Platform, Dimensions } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Button, ButtonVariant, InfoBox, Screen, Spacer, Text } from "../../components";
import { Stack } from "../../components/layout/stack";
import { useFocusEffect, useNavigation, CommonActions } from "@react-navigation/native";
import { Headline, useTheme } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { translate } from "../../i18n/translate";
import { setPaymentCreated } from "../../redux/actions/actionCreators/payment";
import { toHuman } from "../../utils/money";
import { formatDate } from "../../utils/date";
import { HomeParamsList, HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { getUnitNumber } from "../../utils";
import { AppMode, InfoBoxIconType } from "../../enums";
import { navigateToHomePage } from "../../utils/navigateToHomePage";
import { RootState } from "../../redux/reducers";
import { Inline } from "../../components/layout/inline";
import { testProps } from "../../utils/testProps";
import {
  addReservations,
  setBlacklist,
  setBlacklistRecovery,
  updateAppMode,
  setRecoveryPaymentCleared,
  setUserRoleData,
} from "../../redux/actions/actionCreators";
import { redirectToWeb } from "../../utils/domusoGrailsWeb";
import { AppReviewModal } from "../../components/modal/app-review-modal";
import { domusoApiService } from "../../../config";
import { DomusoApiService } from "../../api/DomusoApiService";

const windowWidth = Dimensions.get("window").width;
const windowWidthAdjusted = windowWidth >= 804 ? 804 : windowWidth;

export const PaymentSummary = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const { params = {} as HomeParamsList } = route;
  let {
    propName,
    propertyUnit,
    authorizationDate,
    paymentDate,
    isCertified,
    cardType,
    last4Digits,
    bankInfoText,
    reservationId,
    amounts = {},
    paymentScheduleType,
    createPaymentResult,
    isRecoveryPayment = false,
    hasRemainingDCashRecoveryPayments = false,
    remainingDCashRecoveryPaymentsAmount = 0,
  } = params;
  // These are all bigs
  const { type, headline, infoBoxType, messageBody } = createPaymentResult;
  const { amount, platformFee, total } = amounts;
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const logger = useSelector((state: RootState) => state.logger);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const appMode = useSelector((state: RootState) => state.reservation.appMode);
  const redirectUrl = useSelector((state: RootState) => state.userRole.redirectUrl);
  const [isLoading, setIsLoading] = useState(true);
  const navigateToHome = () => {
    if (Platform.OS === "web") {
      redirectToWeb(redirectUrl);
    } else {
      dispatch(setBlacklist(false));
      dispatch(setBlacklistRecovery(false));
      dispatch(updateAppMode(AppMode.NORMAL));

      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "Home" }],
        })
      );
      return navigateToHomePage({ navigation, params });
    }
  };

  const navigateToMakeAnotherPayment = () => {
    let paymentDetailsScreenName = "EnterPaymentDetails";
    if (appMode == AppMode.DCASH_RECOVERY) {
      paymentDetailsScreenName = "DCashRecoveryEnterPaymentDetails";
    }
    return navigation.navigate(paymentDetailsScreenName, { ...params });
  };

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentCreated(reservationId));
    const userRoleData = async () => {
      const { redirectUrl, isResident } = await domusoApiService.getUserRoles();
      dispatch(setUserRoleData({ isResident, redirectUrl }));
      setIsLoading(false);
    };
    userRoleData();
  }, []);
  useFocusEffect(layoutEffect);

  const formattedPaymentDate = formatDate(paymentDate);
  const formattedAuthorizationDate = formatDate(authorizationDate);
  const formattedAmount = "$" + toHuman(amount);
  const formattedPlatformFee = "$" + toHuman(platformFee);
  const formattedTotal = "$" + toHuman(total);

  if (params.certifiedPaymentType === "DCASH_BA") {
    cardType = "Account ending in";
  }
  const isSuccessfulPayment = type === "SUCCESS";
  useEffect(() => {
    if (isSuccessfulPayment && !hasRemainingDCashRecoveryPayments) {
      dispatch(setBlacklist(false));
      dispatch(setBlacklistRecovery(false));
      dispatch(updateAppMode(AppMode.NORMAL));
      // re-fetch the reservations after we remove the blacklist based on successfull payment
      const getReservations = async () => {
        const response = await domusoApiService.getReservations();
        response[0].stopLoading = true;
        dispatch(addReservations(response));
        //Set isRecoveryPaymentCleared state to true since !hasRemainingDCashRecoveryPayments
        dispatch(setRecoveryPaymentCleared(true));
      };
      getReservations();
    }
  }, []);

  let primaryButtonText = isSuccessfulPayment
    ? "homeStack.returnToHome"
    : "homeStack.makeAnotherPayment";

  if (hasRemainingDCashRecoveryPayments) {
    primaryButtonText = "dcashRecoveryStack.payOutStandingBalanceButtonText";
  }

  const primaryButtonOnPress = isSuccessfulPayment ? navigateToHome : navigateToMakeAnotherPayment;

  const headlineIconType = isSuccessfulPayment
    ? InfoBoxIconType.GREEN_CHECK
    : InfoBoxIconType.RED_X;

  const renderBankInfo = () => {
    return isCertified ? (
      <Inline space="smaller">
        <Image
          style={styles.iconSize}
          source={require("../../../assets/certified_checkmark.png")}
        />
        <Text>{bankInfoText}</Text>
      </Inline>
    ) : (
      bankInfoText
    );
  };

  const dCashRecoveryRelatedInfo = () => {
    if (isRecoveryPayment && hasRemainingDCashRecoveryPayments) {
      return (
        <>
          <Stack>
            <Text style={styles.bodyTextBold}>
              {translate("dcashRecoveryStack.amountOwed", {
                amountOwed: toHuman(remainingDCashRecoveryPaymentsAmount),
              })}
            </Text>
          </Stack>
          <Stack>
            <Text style={styles.bodyText}>
              {translate("dcashRecoveryStack.payOutStandingBalanceBody")}
            </Text>
          </Stack>
        </>
      );
    }
    return null;
  };

  return (
    <Screen
      style={styles.container}
      preset="scroll"
      statusBar="dark-content"
      backgroundColor={theme.colors.background}
      dontUseAutoPadding
      unsafe={true}>
      <View style={styles.screen}>
        <Stack space="medium" style={styles.mainStack}>
          <InfoBox
            infoBoxType={infoBoxType}
            headline={headline}
            headlineIconType={headlineIconType}>
            <Text>{messageBody}</Text>
          </InfoBox>
          <Headline style={styles.paymentSummary}>{translate("homeStack.paymentSummary")}</Headline>
          {paymentScheduleType === "RECURRING" ? (
            <Stack space="smaller">
              <Row styles={styles} title="Unit" info={getUnitNumber(propertyUnit)}></Row>
              <Row styles={styles} title="Total" info="Balance due + Platform Fee"></Row>
              <Row styles={styles} title="Method" info={bankInfoText}></Row>
              <Row
                styles={styles}
                title="Date"
                info={`Monthly starting ${formattedPaymentDate}`}></Row>
            </Stack>
          ) : (
            <Stack space="smaller">
              {propName && <Row styles={styles} title="Property" info={propName} />}
              <Row styles={styles} title="Unit" info={getUnitNumber(propertyUnit)}></Row>
              {authorizationDate && (
                <Row styles={styles} title="Authorization Date" info={formattedAuthorizationDate} />
              )}
              <Row styles={styles} title="Payment Date" info={formattedPaymentDate}></Row>
              <Row styles={styles} title="Method" info={renderBankInfo()} />
              <Row styles={styles} title="Amount" info={formattedAmount}></Row>
              <Row styles={styles} title="Platform Fee" info={formattedPlatformFee}></Row>
              <Row styles={styles} title="Total" info={formattedTotal} isBold></Row>
            </Stack>
          )}
          <Spacer />
          {dCashRecoveryRelatedInfo()}
          <View style={styles.buttonContainer}>
            <Button
              mode="contained"
              loading={isLoading}
              disabled={isLoading}
              tx={primaryButtonText}
              onPress={primaryButtonOnPress}></Button>
          </View>
          <View style={styles.buttonContainer}>
            {!isSuccessfulPayment ? (
              <Button
                loading={isLoading}
                disabled={isLoading}
                onPress={() => navigateToHome()}
                variant={ButtonVariant.LIGHT}
                tx="homeStack.returnToHome"
                mode="contained"
                {...testProps("return-to-home-button")}
              />
            ) : null}
          </View>
          {isSuccessfulPayment && (
            <Stack>
              <AppReviewModal />
            </Stack>
          )}
        </Stack>
      </View>
    </Screen>
  );
};

interface IRowProps extends Record<string, any> {
  styles: any;
  title: string;
  info: any;
  isBold?: boolean;
}

const Row = ({ styles, title, info, isBold = false }: IRowProps) => {
  const content = typeof info === "string" ? <Text>{info}</Text> : info;
  return (
    <View style={styles.confirmPaymentRow}>
      <Text style={styles.rowLabel}>{title}</Text>
      {content}
    </View>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      paddingBottom: spacing.smaller,
      paddingTop: spacing.large,
      flexDirection: "row",
      justifyContent: "center",
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
        },
        default: {
          backgroundColor: colors.lightGrey,
        },
      }),
    },
    screen: {
      justifyContent: "space-between",
      paddingHorizontal: spacing.medium,
      marginTop: -22,
    },
    mainStack: {
      display: "flex",
      flex: 1,
      paddingBottom: 75,
      ...Platform.select({
        native: {
          width: "100%",
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
        },
        default: {
          width: windowWidthAdjusted,
          paddingTop: 25,
          paddingLeft: 25,
          paddingRight: 25,
        },
      }),
    },
    iconSize: {
      height: theme.iconSize.small,
      width: theme.iconSize.small,
      resizeMode: "contain",
    },
    paymentSummary: {
      fontSize: fontSize.medium,
      fontWeight: "600",
      marginBottom: -16,
    },
    rowLabel: {},
    checkboxLabel: {
      maxWidth: 300,
      textAlign: "left",
      fontSize: fontSize.medium,
      // flex: 0.9,
    },
    confirmPaymentRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    bodyText: {
      textAlign: "center",
      fontSize: 16,
      fontWeight: "400",
      marginBottom: 10,
      marginTop: 10,
    },
    bodyTextBold: {
      textAlign: "center",
      fontSize: 16,
      fontWeight: "600",
      marginBottom: 10,
      marginTop: 10,
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: 325,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
  });
};
